import React, { useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { CalendarRoutineFrom } from '@demind-inc/core';

import {
  useAuthContext,
  useSchedulerRoutine,
  selectedDateAtom,
  usePrepareScheduler,
} from '../../../data-access';
import { useRecoilValue } from 'recoil';
import { SchedulerRoutineItem } from '../../Scheduler';
import { translateRoutinePrefToTime } from '../../Scheduler/AIScheduler/reducer/routinePrefItem.state';
import { RoutineInfoModal } from '../../Scheduler/common/RoutineInfoModal/RoutineInfoModal';
import { useGeneralSettings } from '../../../hooks';
import './RoutineSuggestedEventSheetContent.scss';
import { useTranslation } from 'react-i18next';

interface RoutineSuggestedEventSheetContentProps {
  routineFrom: CalendarRoutineFrom;
  onClose: () => void;
  onDeleteRoutine?: () => boolean | void | Promise<boolean | void>;
  disableInteraction?: boolean;
}

const RoutineSuggestedEventSheetContent: React.FC<RoutineSuggestedEventSheetContentProps> = ({
  routineFrom,
  onClose,
  onDeleteRoutine,
  disableInteraction = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();
  const routineId = user?.routineId ?? '';
  const { routine } = useSchedulerRoutine({ routineId });
  const { updateRoutineActivity, hideRoutine } = usePrepareScheduler();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const [isRoutineInfoModalOpen, setIsRoutineInfoModalOpen] = useState(false);

  const previewActivity = useMemo(() => {
    if (!routine?.routineActivities) {
      return undefined;
    }
    return routine.routineActivities.find(
      (activity) => activity.routineActivityId === routineFrom.routineActivityId
    );
  }, [routineFrom, routine]);

  const { value: timeRange = '', smallValue: durationLabel = '' } = useMemo(
    () =>
      previewActivity
        ? translateRoutinePrefToTime(previewActivity, timeFormat)
        : { value: '', smallValue: '' },
    [previewActivity, timeFormat]
  );

  const [startLabel = '', endLabel = ''] = useMemo(
    () => (timeRange.includes('-') ? timeRange.split(' - ') : ['', '']),
    [timeRange]
  );

  const handleRoutineItemPress = () => {
    if (!previewActivity) {
      return;
    }

    setIsRoutineInfoModalOpen(true);
  };

  const handleRoutineSave = async ({ id, activity }: { id?: string; activity: any }) => {
    if (id && user?.preferenceId) {
      await updateRoutineActivity(id, activity);
    }
    setIsRoutineInfoModalOpen(false);
  };

  const handleRoutineDelete = async (activityId: string) => {
    if (onDeleteRoutine) {
      return onDeleteRoutine();
    }

    if (activityId && routineId) {
      await hideRoutine({ routineId, activityId });
      return true;
    }

    return false;
  };

  const handleRoutineInfoModalClose = () => {
    setIsRoutineInfoModalOpen(false);
    // Close the parent modal after a short delay to allow the routine modal to close first
    requestAnimationFrame(() => {
      onClose();
    });
  };

  return (
    <div className="routine-suggested-event-sheet-content">
      {!!previewActivity && (
        <div className="routine-suggested-event-sheet-content__section">
          <Typography className="routine-suggested-event-sheet-content__section-title">
            {t('calendar.suggested.routinePreview')}
          </Typography>
          <SchedulerRoutineItem
            title={previewActivity.name}
            startTime={startLabel}
            endTime={endLabel}
            duration={durationLabel}
            selectable={false}
            showCheckbox={false}
            showScheduleIcon={true}
            onPress={handleRoutineItemPress}
            className="routine-suggested-event-sheet-content__routine-item"
          />
        </div>
      )}

      {isRoutineInfoModalOpen && previewActivity && (
        <RoutineInfoModal
          isOpen={isRoutineInfoModalOpen}
          onClose={handleRoutineInfoModalClose}
          onSave={handleRoutineSave}
          mode="edit"
          selectedDate={selectedDate}
          initialActivity={previewActivity}
          onDelete={handleRoutineDelete}
        />
      )}
    </div>
  );
};

export default RoutineSuggestedEventSheetContent;
